import { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category images - real product images similar to Noon.com style
const categoryImages = {
  'all': 'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=150',
  'mobiles': 'https://images.pexels.com/photos/788946/pexels-photo-788946.jpeg?auto=compress&cs=tinysrgb&w=150',
  'laptops': 'https://images.pexels.com/photos/18105/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=150',
  'gaming': 'https://images.pexels.com/photos/3945659/pexels-photo-3945659.jpeg?auto=compress&cs=tinysrgb&w=150',
  'watches': 'https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=150',
  'perfumes': 'https://images.pexels.com/photos/965989/pexels-photo-965989.jpeg?auto=compress&cs=tinysrgb&w=150',
  'clothing': 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg?auto=compress&cs=tinysrgb&w=150',
  'accessories': 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=150',
  'chocolates': 'https://images.pexels.com/photos/65882/chocolate-dark-coffee-confiserie-65882.jpeg?auto=compress&cs=tinysrgb&w=150',
  'electronics': 'https://images.pexels.com/photos/356056/pexels-photo-356056.jpeg?auto=compress&cs=tinysrgb&w=150',
  'home': 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=150',
  'beauty': 'https://images.pexels.com/photos/2587370/pexels-photo-2587370.jpeg?auto=compress&cs=tinysrgb&w=150',
};

const CategoryNavBar = () => {
  const [categories, setCategories] = useState([]);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollRef = useRef(null);
  const location = useLocation();

  // Hide on products page, homepage, cart page, product detail pages, and wishlist page
  const isProductsPage = location.pathname === '/products';
  const isHomePage = location.pathname === '/';
  const isCartPage = location.pathname === '/cart';
  const isProductDetailPage = location.pathname.startsWith('/product/');
  const isWishlistPage = location.pathname === '/wishlist';
  const isCheckoutPage = location.pathname === '/checkout';
  const isProfilePage = location.pathname === '/profile';
  const isMyOrdersPage = location.pathname === '/my-orders';
  const isOrderTrackingPage = location.pathname.startsWith('/order/');
  const isContactPage = location.pathname === '/contact';
  const isAccountPage = location.pathname === '/account';
  const isCategoriesPage = location.pathname === '/categories';
  const isDealsPage = location.pathname === '/deals';
  const shouldHide = isProductsPage || isHomePage || isCartPage || isProductDetailPage || isWishlistPage || isCheckoutPage || isProfilePage || isMyOrdersPage || isOrderTrackingPage || isContactPage || isAccountPage || isCategoriesPage || isDealsPage;
  
  useEffect(() => {
    if (shouldHide) return; // Don't fetch if hidden
    
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [shouldHide]);

  useEffect(() => {
    if (shouldHide) return;
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, [categories, shouldHide]);

  // Don't render on products page or homepage
  if (shouldHide) return null;

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 200;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  const isActive = (categoryId) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('category') === categoryId;
  };

  const getCategoryImage = (categoryId) => {
    return categoryImages[categoryId] || categoryImages['all'];
  };

  return (
    <nav className="bg-white border-b border-slate-100 relative py-3">
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Left Arrow */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-1.5 hover:bg-slate-50 border border-slate-200"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
        )}

        {/* Categories */}
        <div
          ref={scrollRef}
          onScroll={checkScrollButtons}
          className="flex items-center gap-4 sm:gap-6 overflow-x-auto scrollbar-hide py-2 px-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* All Products */}
          <Link
            to="/products"
            className="flex flex-col items-center gap-2 flex-shrink-0 group"
          >
            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 transition-all duration-200 ${
              location.pathname === '/products' && !location.search
                ? 'border-[#0047AB] shadow-lg shadow-blue-500/20'
                : 'border-slate-200 group-hover:border-[#0047AB]/50'
            }`}>
              <img 
                src={getCategoryImage('all')} 
                alt="All Products"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <span className={`text-xs sm:text-sm font-medium text-center whitespace-nowrap transition-colors ${
              location.pathname === '/products' && !location.search
                ? 'text-[#0047AB]'
                : 'text-slate-600 group-hover:text-[#0047AB]'
            }`}>
              All
            </span>
          </Link>
          
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/products?category=${category.id}`}
              className="flex flex-col items-center gap-2 flex-shrink-0 group"
            >
              <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 transition-all duration-200 ${
                isActive(category.id)
                  ? 'border-[#0047AB] shadow-lg shadow-blue-500/20'
                  : 'border-slate-200 group-hover:border-[#0047AB]/50'
              }`}>
                <img 
                  src={getCategoryImage(category.id)} 
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <span className={`text-xs sm:text-sm font-medium text-center whitespace-nowrap transition-colors ${
                isActive(category.id)
                  ? 'text-[#0047AB]'
                  : 'text-slate-600 group-hover:text-[#0047AB]'
              }`}>
                {category.name}
              </span>
            </Link>
          ))}
        </div>

        {/* Right Arrow */}
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-1.5 hover:bg-slate-50 border border-slate-200"
          >
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        )}
      </div>
    </nav>
  );
};

export default CategoryNavBar;
